import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { AxorButton } from "@/components/primitives";
import { waLink } from "@/config";

const WhatsAppGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Z" />
  </svg>
);

const interests = [
  "Job Opportunities",
  "Career Guidance",
  "Recruitment Services",
  "General Inquiry",
];

const inputClass =
  "w-full rounded-2xl border border-white/[0.1] bg-axor-deep px-5 py-3.5 text-sm text-axor-white outline-none transition-colors placeholder:text-axor-text focus:border-axor-cyan";

export const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: interests[0],
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [ready, setReady] = useState(false);

  const update = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setReady(false);
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = "Please enter a valid email.";
    if (!form.message.trim()) next.message = "Please add a short message.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) setReady(true);
  };

  const whatsappMessage = `Hi Axor Technologies, my name is ${form.name}.%0A%0AI'm interested in: ${form.interest}.%0A%0A${form.message}%0A%0AEmail: ${form.email}${form.phone ? `%0APhone: ${form.phone}` : ""}`;
  const waHref = `https://wa.me/17868272995?text=${whatsappMessage}`;

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="glass-panel rounded-[28px] border border-white/[0.08] p-7 md:p-9"
      data-testid="contact-form"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="flex flex-col gap-2 sm:col-span-1">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-axor-white/50">
            Full Name
          </span>
          <input
            type="text"
            value={form.name}
            onChange={update("name")}
            placeholder="Your name"
            className={inputClass}
            data-testid="contact-name"
          />
          {errors.name && (
            <span className="text-xs text-red-400" data-testid="error-name">
              {errors.name}
            </span>
          )}
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-axor-white/50">
            Email
          </span>
          <input
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="you@email.com"
            className={inputClass}
            data-testid="contact-email"
          />
          {errors.email && (
            <span className="text-xs text-red-400" data-testid="error-email">
              {errors.email}
            </span>
          )}
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-axor-white/50">
            Phone <span className="normal-case text-axor-text">(optional)</span>
          </span>
          <input
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            placeholder="+1 (___) ___-____"
            className={inputClass}
            data-testid="contact-phone"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-axor-white/50">
            I'm Interested In
          </span>
          <select
            value={form.interest}
            onChange={update("interest")}
            className={`${inputClass} cursor-pointer appearance-none`}
            data-testid="contact-interest"
          >
            {interests.map((i) => (
              <option key={i} value={i} className="bg-axor-deep">
                {i}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-2 sm:col-span-2">
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-axor-white/50">
            Message
          </span>
          <textarea
            rows={4}
            value={form.message}
            onChange={update("message")}
            placeholder="Tell us a little about what you're looking for…"
            className={`${inputClass} resize-none`}
            data-testid="contact-message"
          />
          {errors.message && (
            <span className="text-xs text-red-400" data-testid="error-message">
              {errors.message}
            </span>
          )}
        </label>
      </div>

      <div className="mt-7">
        {!ready ? (
          <AxorButton
            type="submit"
            className="w-full sm:w-auto"
            icon={ArrowUpRight}
            data-testid="contact-submit"
          >
            Continue To Axor
          </AxorButton>
        ) : (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col gap-4 rounded-2xl border border-axor-cyan/25 bg-axor-cyan/[0.06] p-5"
              data-testid="contact-ready"
            >
              <p className="flex items-center gap-2 text-sm font-medium text-axor-white">
                <Check className="h-5 w-5 text-axor-turquoise" />
                Your message is ready. Continue on WhatsApp to connect with Axor
                Technologies.
              </p>
              <AxorButton
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
                icon={WhatsAppGlyph}
                data-testid="contact-continue-whatsapp"
              >
                Continue On WhatsApp
              </AxorButton>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </form>
  );
};
